import { SmartComponentType } from "../enum/enumSmartComponentType";
import { ThermostatHVACMode } from "../enum/enumThermostatHVACMode";
import { ApplicationTheme } from "../enum/enumMisc";
import { environment } from "src/environments/environment";

/**
 * Icon helper class to get centralized icon path
 */
export class IconHelper {
  /**
   * Get icon HVAC mode by mode
   *
   * @param hvacMode HVAC mode to get icon
   * @returns
   */
  static getIconHvacModeByMode(hvacMode: ThermostatHVACMode): string {
    let iconPath: string = "";

    if (environment.theme === ApplicationTheme.VIMAR) {
      switch (hvacMode) {
        case ThermostatHVACMode.COMFORT:
          iconPath = "assets/img/vimar/thermostat/ico-comfort-home.svg";
          break;
        case ThermostatHVACMode.STANDBY:
          iconPath = "assets/img/vimar/thermostat/ico-standby-home.svg";
          break;
        case ThermostatHVACMode.ECO:
          iconPath = "assets/img/vimar/thermostat/ico-eco-home.svg";
          break;
        case ThermostatHVACMode.PROTECTION:
          iconPath = "assets/img/vimar/thermostat/ico-protection-home.svg";
          break;
      }
    } else {
      switch (hvacMode) {
        case ThermostatHVACMode.COMFORT:
          iconPath = "assets/img/thermostat/ico-comfort-home.svg";
          break;
        case ThermostatHVACMode.STANDBY:
          iconPath = "assets/img/thermostat/ico-standby-home.svg";
          break;
        case ThermostatHVACMode.ECO:
          iconPath = "assets/img/thermostat/ico-eco-home.svg";
          break;
        case ThermostatHVACMode.PROTECTION:
          iconPath = "assets/img/thermostat/ico-protection-home.svg";
          break;
      }
    }

    return iconPath;
  }

  /**
     * Return path of smart component type icon
     * @param type number of the type of the smart component
     * @returns icon path string
     */
  static getSmartComponentIconByType(type: SmartComponentType): string {
    switch (type) {
      case SmartComponentType.BLINDS:
        return this.getIconsForBlind().blind;
      case SmartComponentType.DIMMER:
        return environment.theme === ApplicationTheme.VIMAR ? this.getIconsForDimmer().on : this.getIconsForDimmer().off;
      case SmartComponentType.LIGHT:
        return environment.theme === ApplicationTheme.VIMAR ? this.getIconsForLightComponent().on : this.getIconsForLightComponent().off;
      case SmartComponentType.THERMOSTAT:
        return this.getIconsForThermostat().thermostat;
      case SmartComponentType.ALARM:
        return environment.theme === ApplicationTheme.VIMAR ? this.getIconsForAlarm().off : this.getIconsForAlarm().on;
      case SmartComponentType.ELECTRIC:
        return this.getIconsForCounters(SmartComponentType.ELECTRIC);
      case SmartComponentType.COLD_WATER:
        return this.getIconsForCounters(SmartComponentType.COLD_WATER);
      case SmartComponentType.HOT_WATER:
        return this.getIconsForCounters(SmartComponentType.HOT_WATER);
      case SmartComponentType.HEATING:
        return this.getIconsForCounters(SmartComponentType.HEATING);
      case SmartComponentType.GAS:
        return this.getIconsForCounters(SmartComponentType.GAS);
      case SmartComponentType.DISTRIBUTOR:
        return this.getIconsForCounters(SmartComponentType.DISTRIBUTOR);
      case SmartComponentType.CONTACT:
        return "assets/img/contact/ico-open-contact.svg";
      case SmartComponentType.SENSOR:
        return this.getIconsForSensor().sensor;
      case SmartComponentType.WEATHER_STATION:
        return this.getIconsForWeatherStation().day;
      case SmartComponentType.LABEL:
        return environment.theme === ApplicationTheme.VIMAR ? "assets/img/vimar/label/label.svg" : "assets/img/label/label.svg";
      case SmartComponentType.SCENARIO:
        return this.getIconsForScenarioComponent().play;
      case SmartComponentType.PAGE_BREAK:
        return environment.theme === ApplicationTheme.VIMAR
          ? "assets/img/vimar/page-break/page-break.svg"
          : "assets/img/page-break/page-break.svg";
      default:
        return "";
    }
  }

  /**
   * Get icons for counters widgets
   * @param type
   * @returns path string
   */
  static getIconsForCounters(type: SmartComponentType): string {
    if (environment.theme === ApplicationTheme.VIMAR) {
      switch (type) {
        case SmartComponentType.ELECTRIC:
          return "assets/img/vimar/counter/electric.svg";
        case SmartComponentType.COLD_WATER:
          return "assets/img/vimar/counter/cold-water.svg";
        case SmartComponentType.HOT_WATER:
          return "assets/img/vimar/counter/hot-water.svg";
        case SmartComponentType.HEATING:
          return "assets/img/vimar/counter/heating.svg";
        case SmartComponentType.GAS:
          return "assets/img/vimar/counter/gas.svg";
        case SmartComponentType.DISTRIBUTOR:
          return "assets/img/vimar/counter/distributor-darker.svg";
      }
    } else {
      switch (type) {
        case SmartComponentType.ELECTRIC:
          return "assets/img/counter/electric.svg";
        case SmartComponentType.COLD_WATER:
          return "assets/img/counter/cold-water.svg";
        case SmartComponentType.HOT_WATER:
          return "assets/img/counter/hot-water.svg";
        case SmartComponentType.HEATING:
          return "assets/img/counter/heating.svg";
        case SmartComponentType.GAS:
          return "assets/img/counter/gas.svg";
        case SmartComponentType.DISTRIBUTOR:
          return "assets/img/counter/distributor.svg";
      }
    }
  }

  /** Get the icons used in the light widget */
  static getIconsForLightComponent(): { [key: string]: string } {
    switch (environment.theme) {
      case ApplicationTheme.VIMAR:
        return {
          on: "assets/img/vimar/light/ico-light-on.svg",
          off: "assets/img/vimar/light/ico-light-off.svg",
        };
      default:
        return {
          on: "assets/img/light/ico-light-on-no-bg.svg",
          off: "assets/img/light/ico-light-off-no-bg.svg",
        };
    }
  }

  /** Get the icons used in the scenario widget */
  static getIconsForScenarioComponent(): { [key: string]: string } {
    switch (environment.theme) {
      case ApplicationTheme.VIMAR:
        // Vimar uses the same icon for both states (like scenario.component);
        // the running state is conveyed by the "with-border" active styling.
        return {
          play: 'assets/img/vimar/play.svg',
          stop: 'assets/img/vimar/play.svg'
        }
      default:
        return {
          play: 'assets/img/scenario/play.svg',
          stop: 'assets/img/scenario/stop.svg'
        }
    }
  }

  /** Get the icons used in the alarm widget */
  static getIconsForAlarm(): { [key: string]: string } {
    switch (environment.theme) {
      case ApplicationTheme.VIMAR:
        return {
          on: 'assets/img/vimar/alarm/ico-alarm-on.svg',
          off: 'assets/img/vimar/alarm/ico-alarm-off.svg'
        }
      default:
        return {
          on: 'assets/img/alarm/ico-alarm-on.svg',
          off: 'assets/img/alarm/ico-alarm-off.svg'
        }

    }
  }

  /** Get the icons used in the dimmer widget */
  static getIconsForDimmer(): { [key: string]: string } {
    switch (environment.theme) {
      case ApplicationTheme.VIMAR:
        return {
          on: "assets/img/vimar/dimmer/ico-dimmer-on.svg",
          off: "assets/img/vimar/dimmer/ico-dimmer-off.svg",
          sliderOn: "assets/img/vimar/dimmer/ico-dimmer-slider-on.svg",
          sliderOff: "assets/img/vimar/dimmer/ico-dimmer-slider-off.svg",
          fault: "assets/img/vimar/dimmer/warning.svg",
        };
      default:
        return {
          on: "assets/img/dimmer/ico-dimmer-on-no-bg.svg",
          off: "assets/img/dimmer/ico-dimmer-off-no-bg.svg",
          sliderOn: "assets/img/dimmer/ico-dimmer-on-no-bg.svg",
          sliderOff: "assets/img/dimmer/ico-dimmer-off-no-bg.svg",
          fault: "assets/img/dimmer/warning.svg",
        };
    }
  }

  /** Get the icons used in the sensor widget */
  static getIconsForSensor(): { [key: string]: string } {
    switch (environment.theme) {
      case ApplicationTheme.VIMAR:
        return {
          sensor: "assets/img/vimar/sensor/ico-sensor.svg",
          brightnessLow: "assets/img/vimar/sensor/ico-brightness-low.svg",
          brightnessHigh: "assets/img/vimar/sensor/ico-brightness-high.svg",
          colorTemperatureSlider:
            "assets/img/vimar/sensor/color-temperature-slider.svg",
          autoBrightness: "assets/img/vimar/dimmer/ico-dimmer-slider-off.svg",
          faultDimmer: "assets/img/vimar/sensor/fault-state-dimmer.svg",
        };
      default:
        return {
          sensor: "assets/img/sensor/ico-sensor.svg",
          brightnessLow: "assets/img/sensor/ico-brightness-low.svg",
          brightnessHigh: "assets/img/sensor/ico-brightness-high.svg",
          colorTemperatureSlider:
            "assets/img/sensor/color-temperature-slider.svg",
          autoBrightness: "assets/img/dimmer/ico-dimmer-on-no-bg.svg",
          faultDimmer: "assets/img/sensor/fault-state-dimmer.svg",
        };
    }
  }

  /** Get the icons used in the weather station widget */
  static getIconsForWeatherStation(): { [key: string]: string } {
    switch (environment.theme) {
      case ApplicationTheme.VIMAR:
        return {
          day: "assets/img/vimar/weather-station/sunny.svg",
          night: "assets/img/vimar/weather-station/moon.svg",
          warningWind: "assets/img/vimar/weather-station/warning-wind.svg",
          warningRain: "assets/img/vimar/weather-station/warning-rain.svg",
          wind: "assets/img/vimar/weather-station/wind.svg",
          sunrise: "assets/img/vimar/weather-station/sunrise.svg",
          sunset: "assets/img/vimar/weather-station/sunset.svg",
          temperature: "assets/img/vimar/weather-station/temperature.svg",
          brightness: "assets/img/vimar/dimmer/ico-dimmer-slider-off.svg",
        };
      default:
        return {
          day: "assets/img/weather-station/sunny.svg",
          night: "assets/img/weather-station/moon.svg",
          warningWind: "assets/img/weather-station/warning-wind.svg",
          warningRain: "assets/img/weather-station/warning-rain.svg",
          wind: "assets/img/weather-station/wind.svg",
          sunrise: "assets/img/weather-station/sunrise.svg",
          sunset: "assets/img/weather-station/sunset.svg",
          temperature: "assets/img/weather-station/temperature.svg",
          brightness: "assets/img/weather-station/brightness.svg",
        };
    }
  }

  /** Get the icons used in the blind widget */
  static getIconsForBlind(): { [key: string]: string } {
    switch (environment.theme) {
      case ApplicationTheme.VIMAR:
        return {
          blind: "assets/img/vimar/blind/blind.svg",
          open: "assets/img/vimar/blind/open.svg",
          close: "assets/img/vimar/blind/close.svg",
          stop: "assets/img/vimar/blind/stop.svg",
          sliderOffHeight: "assets/img/vimar/blind/blind-up.svg",
          sliderOnHeight: "assets/img/vimar/blind/blind-down.svg",
          sliderOffSlat: "assets/img/vimar/blind/slat-horizontal.svg",
          sliderOnSlat: "assets/img/vimar/blind/slat-45.svg",
        };
      default:
        return {
          blind: "assets/img/blind/ico-blind-close-no-bg.svg",
          open: "assets/img/blind/ico-blind-open.svg",
          close: "assets/img/blind/ico-blind-close.svg",
          stop: "assets/img/blind/ico-blind-stop.svg",
          sliderOffHeight: "assets/img/blind/ico-blind-open-no-bg.svg",
          sliderOnHeight: "assets/img/blind/ico-blind-close-no-bg.svg",
          sliderOffSlat: "assets/img/blind/ico-slat-horizontal.svg",
          sliderOnSlat: "assets/img/blind/ico-slat-45.svg",
        };
    }
  }

  /** Get the icons used in the thermostat widget */
  static getIconsForThermostat(): { [key: string]: string } {
    switch (environment.theme) {
      case ApplicationTheme.VIMAR:
        return {
          thermostat: "assets/img/vimar/thermostat/ico-thermostat-main.svg",
          summer: "assets/img/vimar/thermostat/ico-summer.svg",
          winter: "assets/img/vimar/thermostat/ico-winter.svg",
          valve: "/assets/img/vimar/thermostat/ico-valve.svg",
          openWindow: "/assets/img/vimar/thermostat/ico-window.svg",
          fancoil: "/assets/img/vimar/thermostat/ico-fancoil.svg",
          temperatureLow: "assets/img/vimar/thermostat/ico-thermostat-low.svg",
          temperatureHigh:
            "assets/img/vimar/thermostat/ico-thermostat-high.svg",
          setpointDown: "assets/img/vimar/thermostat/shift-setpoint-down.svg",
          setpointUp: "assets/img/vimar/thermostat/shift-setpoint-up.svg",
        };
      default:
        return {
          thermostat: "assets/img/thermostat/ico-thermostat-main.svg",
          summer: "assets/img/thermostat/ico-summer.svg",
          winter: "assets/img/thermostat/ico-winter.svg",
          valve: "/assets/img/thermostat/ico-valve.svg",
          openWindow: "/assets/img/thermostat/ico-window-open.svg",
          fancoil: "/assets/img/thermostat/ico-fancoil.svg",
          temperatureLow: "assets/img/thermostat/ico-thermostat.svg",
          temperatureHigh: "assets/img/thermostat/ico-thermostat-high.svg",
          setpointDown: "assets/img/thermostat/shift-setpoint-down.svg",
          setpointUp: "assets/img/thermostat/shift-setpoint-up.svg",
        };
    }
  }
}
